import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  orderBy,
  where,
  limit,
} from 'firebase/firestore';
import { firestore } from 'services/firebase/client';
import type { MonthlyEdition } from 'utils/types/MonthlyEdition';

const COLLECTION = 'monthly_editions';

/**
 * Busca a edição mensal atual (isCurrent === true).
 * Usado na página de Cervejas do Mês no site.
 */
export async function getCurrentMonthlyEdition(): Promise<MonthlyEdition | null> {
  try {
    const coll = collection(firestore, COLLECTION);
    const q = query(coll, where('isCurrent', '==', true), limit(1));
    const snapshot = await getDocs(q);
    if (snapshot.empty) return null;
    const docSnap = snapshot.docs[0];
    const data = docSnap.data();
    return {
      month: data.month,
      label: data.label,
      beers: data.beers ?? [],
      glasses: data.glasses ?? [],
      isCurrent: data.isCurrent,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    } as MonthlyEdition;
  } catch (error) {
    console.error('Erro ao buscar edição mensal atual:', error);
    return null;
  }
}

/**
 * Busca uma edição pelo mês (ex: "2025-02").
 * Útil para histórico ou dashboard.
 */
export async function getMonthlyEditionByMonth(
  monthId: string,
): Promise<MonthlyEdition | null> {
  try {
    const ref = doc(firestore, COLLECTION, monthId);
    const snapshot = await getDoc(ref);
    if (!snapshot.exists()) return null;
    const data = snapshot.data();
    return {
      month: data.month,
      label: data.label,
      beers: data.beers ?? [],
      glasses: data.glasses ?? [],
      isCurrent: data.isCurrent,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    } as MonthlyEdition;
  } catch (error) {
    console.error(`Erro ao buscar edição ${monthId}:`, error);
    return null;
  }
}

/**
 * Lista todas as edições ordenadas por mês (mais recente primeiro).
 * Útil para histórico no dashboard.
 */
export async function listMonthlyEditions(): Promise<MonthlyEdition[]> {
  try {
    const coll = collection(firestore, COLLECTION);
    const q = query(coll, orderBy('month', 'desc'));
    const snapshot = await getDocs(q);
    return snapshot.docs.map(docSnap => {
      const data = docSnap.data();
      return {
        month: data.month,
        label: data.label,
        beers: data.beers ?? [],
        glasses: data.glasses ?? [],
        isCurrent: data.isCurrent,
        createdAt: data.createdAt,
        updatedAt: data.updatedAt,
      } as MonthlyEdition;
    });
  } catch (error) {
    console.error('Erro ao listar edições mensais:', error);
    return [];
  }
}

/**
 * Lista somente as edições anteriores, da mais recente para a mais antiga.
 */
export async function listPreviousMonthlyEditions(): Promise<MonthlyEdition[]> {
  const editions = await listMonthlyEditions();
  return editions.filter(edition => !edition.isCurrent);
}
